import * as Eden from '@eden';
import _ from 'lodash';
import { compile } from 'path-to-regexp';
import qs from 'qs';
import Config from '@util/config';
import type { AllowedCapabilityTokenType, CapabilityParam } from './capabilityTokenHelper';
import { getCapabilityTokenHeader } from './capabilityTokenHelper';

import { makeAsyncAction } from './make-async-action';
import { ResponseParseMethod } from './types';

export type QueryParams = Record<string, string | number | boolean>;

export function makeQueryString(queryParams: QueryParams) {
    const queryString = qs.stringify(queryParams, {
        encoder: (str, defaultEncoder, charset, type) => {
            if (type === 'key') return defaultEncoder(_.snakeCase(str), charset);
            return defaultEncoder(str, charset);
        },
    });
    return queryString.length === 0 ? '' : `?${queryString}`;
}
// TODO: handle undefined and empty string parameters properly
export const makeURL = (path: string, pathParams: Record<string, string>, queryParams: QueryParams) => {
    const pathString = compile(path)(pathParams);
    const queryString = makeQueryString(queryParams);
    return `${Config.EDEN_BASE_URL}${pathString}${queryString}`;
};

type PathParams<RN extends Eden.RouteName, R extends Eden.Routes[RN]> = R['params'] extends []
    ? {
          pathParams?: {};
      }
    : {
          pathParams: { [param in R['params'][number]]: string };
      };

type Body<
    RN extends Eden.RouteName,
    R extends Eden.Routes[RN],
> = Eden.InputContent[R['inputType']] extends typeof Eden.NO_CONTENT
    ? {
          body?: Eden.InputContent[R['inputType']] | typeof Eden.NO_CONTENT;
      }
    : {
          body: Eden.InputContent[R['inputType']];
      };

type RequestOption<RouteName extends Eden.RouteName, R extends Eden.Routes[RouteName]> = PathParams<RouteName, R> &
    Body<RouteName, R> &
    CapabilityParam<AllowedCapabilityTokenType<R>> & {
        headers?: Record<string, any>;
        queryParams?: QueryParams;
    };
export function makeEdenAction<RouteName extends Eden.RouteName, R extends Eden.Routes[RouteName]>(
    routeName: RouteName,
    responseParseMethod?: ResponseParseMethod,
) {
    const { path, method, auth, inputType, outputType, paginate } = Eden.routes[routeName] as R;

    const headers = Eden.generateEdenHeaders({ inputType, outputType, paginate });

    const setupRequest = ({
        pathParams = {},
        body = Eden.NO_CONTENT,
        headers: extraHeaders = {},
        queryParams = {},
        capabilityToken,
    }: RequestOption<RouteName, R>) => ({
        url: makeURL(path, pathParams, queryParams),
        method,
        auth,
        body,
        headers: { ...headers, ...extraHeaders, ...getCapabilityTokenHeader(capabilityToken) },
        paginate,
        responseParseMethod: responseParseMethod || getDefaultResponseParseMethod(outputType),
    });

    const handleResponse = (
        payload: R['outputType'] extends Eden.ListContentType<any>
            ? Eden.OutputContent[R['outputType']['type']][]
            : R['outputType'] extends Eden.OutputContentType
              ? Eden.OutputContent[R['outputType']]
              : void,
    ) => payload;

    return makeAsyncAction(
        `api/${routeName}_REQUEST`,
        `api/${routeName}_SUCCESS`,
        `api/${routeName}_FAILURE`,
        setupRequest,
        handleResponse,
    );
}

function getDefaultResponseParseMethod(
    outputType: Eden.OutputContentType | Eden.ListContentType<Eden.OutputContentType>,
): ResponseParseMethod {
    if (outputType === Eden.OutputContentType.NONE) {
        return ResponseParseMethod.NONE;
    } else {
        return ResponseParseMethod.JSON;
    }
}
